import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { projectsAPI, projectsAssignmentsAPI, adminAPI, translationsAPI } from '../services/api';
import { hasPermission } from '../utils/auth';
import { t } from '../utils/i18n';

function Projects() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, forceUpdate] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [projectName, setProjectName] = useState('');
    const [projectDescription, setProjectDescription] = useState('');
    const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [users, setUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState('');

    useEffect(() => {
        loadProjects();
        if (searchParams.get('action') === 'new') {
            setShowCreateModal(true);
        }
        const { getAuth } = require('../utils/auth');
        const { user } = getAuth();
        if (user?.role === 'admin') {
            loadUsers().catch(err => {
                // Silently fail if not admin
                console.log('Cannot load users (not admin):', err);
            });
        }
    }, [searchParams]);

    const loadUsers = async () => {
        try {
            const response = await adminAPI.getUsers();
            if (response.data.success) {
                setUsers(response.data.users || []);
            }
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };

    const loadAssignments = async (projectId) => {
        try {
            const response = await projectsAssignmentsAPI.getAssignments(projectId);
            if (response.data.success) {
                setAssignments(response.data.assignments || []);
            }
        } catch (error) {
            console.error('Error loading assignments:', error);
        }
    };

    const handleOpenAssignments = async (project) => {
        setSelectedProject(project);
        setShowAssignmentsModal(true);
        await loadAssignments(project.id);
    };

    const handleAssignUser = async () => {
        if (!selectedUserId || !selectedProject) return;
        try {
            await projectsAssignmentsAPI.assignUser(selectedProject.id, parseInt(selectedUserId));
            setSelectedUserId('');
            await loadAssignments(selectedProject.id);
            alert(t('saved'));
        } catch (error) {
            alert(error.response?.data?.error || t('error'));
        }
    };

    const handleRemoveAssignment = async (userId) => {
        if (!selectedProject) return;
        if (!window.confirm(t('confirmRemoveAssignment'))) return;
        try {
            await projectsAssignmentsAPI.removeAssignment(selectedProject.id, userId);
            await loadAssignments(selectedProject.id);
            alert(t('saved'));
        } catch (error) {
            alert(error.response?.data?.error || t('error'));
        }
    };

    useEffect(() => {
        const handleLanguageChange = () => {
            forceUpdate(prev => prev + 1);
        };
        window.addEventListener('languageChanged', handleLanguageChange);
        return () => window.removeEventListener('languageChanged', handleLanguageChange);
    }, []);

    const loadProjects = async () => {
        try {
            setLoading(true);
            const { getAuth } = require('../utils/auth');
            const { user } = getAuth();
            console.log('🔍 Loading projects for user:', user?.username, 'role:', user?.role);
            
            const response = await projectsAPI.getAll();
            console.log('🔍 Projects API response:', response.data);
            
            if (response.data.success) {
                const allProjects = response.data.projects || [];
                console.log('✅ Loaded projects:', allProjects.length, allProjects);
                setProjects(allProjects);
            } else {
                console.error('❌ Failed to load projects:', response.data);
                setProjects([]);
            }
        } catch (error) {
            console.error('❌ Error loading projects:', error);
            console.error('Error details:', error.response?.data);
            setProjects([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateProject = async (e) => {
        e.preventDefault();
        if (!projectName.trim()) {
            alert(t('projectNameRequired'));
            return;
        }

        try {
            const response = await projectsAPI.create({
                name: projectName,
                description: projectDescription
            });

            if (response.data.success) {
                setShowCreateModal(false);
                setProjectName('');
                setProjectDescription('');
                setSearchParams({});
                loadProjects();
                alert(t('saved'));
            }
        } catch (error) {
            alert(error.response?.data?.error || t('error'));
        }
    };

    const handleDeleteProject = async (projectId) => {
        const { getAuth } = require('../utils/auth');
        const { user } = getAuth();
        if (user?.role !== 'admin') {
            alert(t('noPermission'));
            return;
        }

        if (!window.confirm(t('confirmDeleteProject'))) {
            return;
        }

        try {
            const response = await projectsAPI.delete(projectId);
            if (response.data.success) {
                loadProjects();
                alert(t('projectDeleted'));
            }
        } catch (error) {
            alert(error.response?.data?.error || t('error'));
        }
    };

    const handleExportProject = async (projectId) => {
        const { getAuth } = require('../utils/auth');
        const { user } = getAuth();
        if (user?.role !== 'admin') {
            alert(t('noPermission'));
            return;
        }

        try {
            const response = await translationsAPI.exportProject(projectId);
            
            // Create blob and download
            const blob = new Blob([response.data], { 
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Get project name for filename
            const project = projects.find(p => p.id === projectId);
            const filename = project ? `${project.name}.xlsx` : `translations_${projectId}.xlsx`;
            link.setAttribute('download', filename);
            
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            alert(t('exportSuccess'));
        } catch (error) {
            console.error('Export error:', error);
            alert(error.response?.data?.error || t('exportError'));
        }
    };

    if (loading) {
        return (
            <Layout>
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <div className="loading" style={{ width: '40px', height: '40px', margin: '0 auto' }}></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="content-header">
                <h1 className="page-title">{t('projects')}</h1>
                <p className="page-description">{t('manageProjects')}</p>
            </div>

            {(() => {
                const { getAuth } = require('../utils/auth');
                const { user } = getAuth();
                const isAdmin = user?.role === 'admin';
                return isAdmin && (
                    <div className="table-actions" style={{ marginBottom: '24px' }}>
                        <button 
                            onClick={() => setShowCreateModal(true)}
                            className="btn btn-primary"
                        >
                            <i className="fas fa-plus"></i>
                            <span>{t('createProject')}</span>
                        </button>
                    </div>
                );
            })()}

            <div className="table-container">
                {(() => {
                    const { getAuth } = require('../utils/auth');
                    const { user } = getAuth();
                    const isAdmin = user?.role === 'admin';
                    return isAdmin && (
                        <div className="table-header">
                            <h2 className="table-title">{t('allProjects')}</h2>
                        </div>
                    );
                })()}
                
                {projects.length > 0 ? (
                    (() => {
                        const { getAuth } = require('../utils/auth');
                        const { user } = getAuth();
                        const isAdmin = user?.role === 'admin';
                        
                        // For facilitator and other roles, show simple list with project names only
                        if (!isAdmin) {
                            return (
                                <div style={{ display: 'grid', gap: '12px' }}>
                                    {projects.map((project) => (
                                        <Link 
                                            key={project.id}
                                            to={`/translations/${project.id}`}
                                            style={{
                                                display: 'block',
                                                padding: '20px',
                                                background: 'var(--bg-color)',
                                                borderRadius: '8px',
                                                border: '1px solid var(--border-color)',
                                                textDecoration: 'none',
                                                color: 'var(--text-primary)',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'var(--bg-secondary)';
                                                e.currentTarget.style.borderColor = 'var(--brand-primary)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'var(--bg-color)';
                                                e.currentTarget.style.borderColor = 'var(--border-color)';
                                            }}
                                        >
                                            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                                                {project.name}
                                            </h3>
                                        </Link>
                                    ))}
                                </div>
                            );
                        }
                        
                        // For admin, show full table
                        console.log('🔍 Admin view - rendering delete buttons for', projects.length, 'projects');
                        return (
                            <div className="table-responsive">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>{t('name')}</th>
                                            <th>{t('file')}</th>
                                            <th>{t('creator')}</th>
                                            <th>{t('pending')}</th>
                                            <th>{t('status')}</th>
                                            <th>{t('action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {projects.map((project) => (
                                            <tr key={project.id}>
                                                <td>
                                                    <strong>{project.name}</strong>
                                                    {project.description && (
                                                        <>
                                                            <br />
                                                            <small style={{ color: 'var(--gray-600)' }}>
                                                                {project.description}
                                                            </small>
                                                        </>
                                                    )}
                                                </td>
                                                <td>{project.fileName || '-'}</td>
                                                <td>{project.creatorName || '-'}</td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <div style={{ flex: 1, background: '#eee', height: '8px', borderRadius: '4px', overflow: 'hidden' }}>
                                                            <div 
                                                                style={{ 
                                                                    width: `${project.progress}%`, 
                                                                    height: '100%', 
                                                                    background: project.progress === 100 ? '#60AD9B' : '#CF9B06' 
                                                                }}
                                                            ></div>
                                                        </div>
                                                        <span style={{ fontSize: '12px', color: '#666' }}>{project.progress}%</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`status-badge ${project.status === 'active' ? 'confirmed' : 'pending'}`}>
                                                        {project.status === 'active' ? t('active') : t('inactive')}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                    <Link 
                                                        to={`/translations/${project.id}`} 
                                                        className="btn btn-outline btn-sm"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                        <span>{t('edit')}</span>
                                                    </Link>
                                                        <button
                                                            onClick={() => handleOpenAssignments(project)}
                                                            className="btn btn-outline btn-sm"
                                                            title={t('assignUsers')}
                                                        >
                                                            <i className="fas fa-users"></i>
                                                        </button>
                                                        <button
                                                            onClick={() => handleExportProject(project.id)}
                                                            className="btn btn-outline btn-sm"
                                                            title={t('exportToExcel')}
                                                        >
                                                            <i className="fas fa-download"></i>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteProject(project.id)}
                                                            className="btn btn-outline btn-sm"
                                                            title={t('deleteProject')}
                                                            style={{ 
                                                                color: '#dc3545',
                                                                borderColor: '#dc3545',
                                                                minWidth: '40px'
                                                            }}
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })()
                ) : (
                    <div className="empty-state">
                        <div className="empty-icon">
                            <i className="fas fa-folder-open"></i>
                        </div>
                        <h3 className="empty-title">{t('noProjects')}</h3>
                        <p className="empty-description">
                            {(() => {
                                const { getAuth } = require('../utils/auth');
                                const { user } = getAuth();
                                return user?.role === 'admin' ? t('startNewProject') : t('noProjectsDescription');
                            })()}
                        </p>
                        {(() => {
                            const { getAuth } = require('../utils/auth');
                            const { user } = getAuth();
                            return user?.role === 'admin' && (
                                <button 
                                    onClick={() => setShowCreateModal(true)}
                                    className="btn btn-primary"
                                    style={{ marginTop: '20px' }}
                                >
                                    <i className="fas fa-plus"></i>
                                    <span>{t('createProject')}</span>
                                </button>
                            );
                        })()}
                    </div>
                )}
            </div>

            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3 className="modal-title">{t('createProject')}</h3>
                        </div>
                        <form onSubmit={handleCreateProject}>
                            <div className="form-group">
                                <label className="form-label">{t('projectName')} *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={projectName}
                                    onChange={(e) => setProjectName(e.target.value)}
                                    placeholder={t('projectName')}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">{t('description')}</label>
                                <textarea
                                    className="form-textarea"
                                    value={projectDescription}
                                    onChange={(e) => setProjectDescription(e.target.value)}
                                    placeholder={t('description')}
                                    rows={4}
                                />
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-outline"
                                    onClick={() => setShowCreateModal(false)}
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                >
                                    <i className="fas fa-plus"></i>
                                    <span>{t('create')}</span>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
}

export default Projects;

